import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useLocale } from '../contexts/LocaleContext'
import { resolveLocale, setStoredLocale } from '../i18n/detect-locale'
import { getUserProfile } from '../services/api/profile'
import { getAccessToken } from '../services/authentication'
import { isMobileApp } from '../utils/webviewBridge'

// directoryApiClient sets no axios timeout, so a hung/slow endpoint would otherwise
// leave the profile fetch (and any caller awaiting it) pending forever. Mirrors the
// 2s AbortController cap used for the BR probe in CredentialsForm.tsx.
const PROFILE_FETCH_TIMEOUT_MS = 2000

/**
 * Fetches the Directory-stored locale (users.language) for the currently authenticated
 * account and applies it via the given setLocale. Returns true only when the profile
 * was actually fetched and applied; false when skipped (native webview, no token yet)
 * or when the fetch failed or timed out.
 *
 * Skipped in the native app webview, where the device/app locale (navigator.language)
 * stays authoritative — mirrors the isMobileApp() gate applied in the other Travel
 * fronts (okarito-front syncFromDirectory, employee AppProvider).
 *
 * Opts out of the shared axios client's refresh-on-401 handling (`skipAuthRefresh`):
 * this fetch is a best-effort background sync, not a user-initiated action, so it must
 * never be able to force-sign-out an otherwise-valid session over a stray/transient 401.
 *
 * Exported so call sites that render an authenticated screen without a route change
 * (e.g. the post-login passkey prompt in BaseSignInRoute, which flips local state
 * rather than navigating) can force the sync immediately instead of waiting for
 * useDirectoryLocaleSync's next navigation-triggered retry. Such call sites should
 * NOT await this before rendering: it fetches over the network and must never block
 * (or delay) a screen that's otherwise ready to show — fire it alongside the render.
 */
export const syncDirectoryLocale = async (setLocale: (locale: string) => void): Promise<boolean> => {
  if (isMobileApp()) return false
  const accessToken = getAccessToken()
  if (!accessToken) return false

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), PROFILE_FETCH_TIMEOUT_MS)
  try {
    const profile = await getUserProfile(accessToken, { signal: controller.signal, skipAuthRefresh: true })
    const resolved = resolveLocale(profile.locale)
    if (resolved) {
      setStoredLocale(resolved)
      setLocale(resolved)
    }
    return true
  } catch {
    // Non-blocking: keep the cached/browser locale if the profile fetch fails or times out.
    return false
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Headless hook that syncs the Directory-stored locale (users.language) into the
 * local UI once per authenticated session.
 *
 * Resolution priority after this sync (TRAVEL-4158):
 *   1. Directory users.language  ← this hook writes to localStorage + LocaleContext
 *   2. localStorage cache        ← picked up by getLocale() on subsequent boots
 *   3. navigator.languages       ← browser fallback when no preference is stored
 *
 * The access token is read from a cookie, which may still be empty at first render
 * (e.g. a session that authenticates client-side via exchangeToken before landing on
 * an authenticated route). To avoid missing that late token, the sync is retried on
 * every navigation until it finds a token, then applied exactly once per session
 * (guarded by hasSyncedRef).
 *
 * The sync is fire-and-forget: failures are silently swallowed so a network error
 * never breaks the UI. On failure hasSyncedRef stays false, so the next navigation
 * retries — the session is not permanently blocked by a transient error.
 *
 * Must be called inside LocaleProvider and a Router.
 */
export const useDirectoryLocaleSync = (): void => {
  const { setLocale } = useLocale()
  const { pathname } = useLocation()
  const hasSyncedRef = useRef(false)
  const syncingRef = useRef(false)

  useEffect(() => {
    // Native app webview: keep the device/app locale (navigator.language) authoritative.
    if (isMobileApp()) return
    // Already applied this session: the Directory preference is a one-shot.
    // In-flight guard: skip concurrent fetches triggered by rapid navigation.
    if (hasSyncedRef.current || syncingRef.current) return
    // No token yet: retry on the next navigation once auth has resolved. Checked here
    // (not just inside syncDirectoryLocale) so syncingRef is only held while a fetch is
    // actually in flight.
    if (!getAccessToken()) return

    syncingRef.current = true
    syncDirectoryLocale(setLocale)
      .then((didSync) => {
        // A token was already confirmed above, so false here only means the fetch
        // failed or timed out — stays false so the next navigation retries.
        hasSyncedRef.current = didSync
      })
      .finally(() => {
        syncingRef.current = false
      })
  }, [pathname, setLocale]) // retry on each navigation; setLocale is stable (useState setter)
}
